// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Injectable, inject, TemplateRef } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { environment } from '@environment';
import { Column, DataRow } from '@lfx/shared/interfaces';
import { CompanyService } from '@lfx/core/services';
import {
  MetricChartEntityService,
  MetricChartData,
  MetricChartApiResponse
} from './metric-chart-base.entity';
import { TEMPLATE_IDENTIFIERS } from '@lfx/key-projects/key-projects.model';

/**
 * Event speaker data model from SQL query (API returns uppercase keys)
 */
export interface EventSpeakerData {
  EVENT_ID: string;
  EVENT_NAME: string;
  EVENT_START_DATE: string;
  EVENT_URL: string;
  SPEAKER_ID: string;
  SPEAKER_FIRST_NAME: string;
  SPEAKER_LAST_NAME: string;
  SPEAKER_TITLE: string;
  SPEAKER_PHOTO_URL?: string;
  SPEAKER_STATUS: string;
  ACCOUNT_ID: string;
  ACCOUNT_NAME: string;
  PROJECT_ID: string;
  PROJECT_NAME: string;
  PROJECT_SLUG: string;
  FOUNDATION_ID: string;
  FOUNDATION_NAME: string;
}

/**
 * Event speaker summary data model from SQL query
 */
export interface EventSpeakerSummaryData {
  totalEventSpeakers: number;
}

/**
 * Entity service for event speakers metric type
 */
@Injectable({
  providedIn: 'root'
})
export class EventSpeakersMetricEntity implements MetricChartEntityService {
  private readonly http = inject(HttpClient);
  private readonly companyService = inject(CompanyService);

  private readonly config = {
    definition: "Count of speakers and breakout session speakers at LF events for this project's foundation.",
    columns: [
      { name: 'Our event speakers', prop: 'displayName', templateId: TEMPLATE_IDENTIFIERS.USER_PROFILE },
      { name: 'Event name', prop: 'eventName' },
      { name: 'Event date', prop: 'eventDate' }
    ],
    summaryColumns: [
      { name: 'Definition', prop: 'definition' },
      { name: 'Total count for this project', prop: 'totalCount' },
      { name: 'Data source', prop: 'dataSource' }
    ],
    dataSource: 'LFX'
  };

  /**
   * Get columns for event speakers table
   * Based on UI requirements for event speakers display
   */
  getColumns(): Column[] {
    return this.config.columns.map(col => {
      const { templateId, ...columnProps } = col;
      return {
        ...columnProps,
        ...(templateId && { template: templateId as unknown as TemplateRef<unknown> })
      };
    });
  }

  /**
   * Get API endpoint for event speakers
   */
  getEndpoint(): string {
    return `${environment.apiUrl}/event-speakers`;
  }

  /**
   * Fetch event speakers data from backend
   */
  fetchData(params: MetricChartData): Observable<DataRow[]> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error('No organization ID found');
        }

        const httpParams = new HttpParams()
          .set('accountId', company.salesforceB2BAccountId)
          .set('projectSlug', params.projectSlug || '');

        return this.http.get<MetricChartApiResponse<EventSpeakerData>>(
          this.getEndpoint(),
          { params: httpParams }
        ).pipe(
          map(response => {
            if (!response.success) {
              throw new Error(response.message || 'Failed to fetch event speakers data');
            }

            return response.data.map(speaker => this.transformToDataRow(speaker));
          }),
          catchError(error => {
            console.error('Error fetching event speakers data:', error);
            return of([]);
          })
        );
      }),
      catchError(error => {
        console.error('Error in event speakers entity service:', error);
        return of([]);
      })
    );
  }

  /**
   * Transform event speaker data to DataRow format for the table
   */
  private transformToDataRow(speaker: EventSpeakerData): DataRow {
    return {
      displayName: {
        text: `${speaker.SPEAKER_FIRST_NAME} ${speaker.SPEAKER_LAST_NAME}`,
        extraData: {
          imageUrl: speaker.SPEAKER_PHOTO_URL,
          userId: speaker.SPEAKER_ID,
          memberId: speaker.SPEAKER_ID,
          memberDisplayName: `${speaker.SPEAKER_FIRST_NAME} ${speaker.SPEAKER_LAST_NAME}`,
          memberLogoUrl: speaker.SPEAKER_PHOTO_URL
        }
      },
      eventName: { text: speaker.EVENT_NAME },
      eventDate: { text: this.formatDate(speaker.EVENT_START_DATE) }
    };
  }

  /**
   * Fetch event speakers summary data from backend
   */
  getSummaryData(params: MetricChartData): Observable<{columns: Column[], data: DataRow[]}> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error('No organization ID found');
        }

        const httpParams = new HttpParams()
          .set('projectSlug', params.projectSlug || '');

        return this.http.get<{success: boolean; message?: string; data: EventSpeakerSummaryData}>(
          `${this.getEndpoint()}/summary`,
          { params: httpParams }
        ).pipe(
          map(response => {
            if (!response.success) {
              throw new Error(response.message || 'Failed to fetch event speakers summary');
            }

            // Define columns for summary table
            const summaryColumns: Column[] = this.config.summaryColumns;

            // Transform API response to summary table format
            const summaryData: DataRow[] = [
              {
                definition: { text: this.config.definition },
                totalCount: { text: response.data.totalEventSpeakers.toLocaleString('en-US') },
                dataSource: { text: this.config.dataSource }
              }
            ];

            return { columns: summaryColumns, data: summaryData };
          }),
          catchError(error => {
            console.error('Error fetching event speakers summary:', error);
            // Return fallback data on error
            const fallbackColumns: Column[] = this.config.summaryColumns;
            const fallbackData: DataRow[] = [
              {
                definition: { text: this.config.definition },
                totalCount: { text: 'N/A' },
                dataSource: { text: this.config.dataSource }
              }
            ];
            return of({ columns: fallbackColumns, data: fallbackData });
          })
        );
      }),
      catchError(error => {
        console.error('Error in event speakers summary entity service:', error);
        // Return fallback data
        const fallbackColumns: Column[] = this.config.summaryColumns;
        const fallbackData: DataRow[] = [
          {
            definition: { text: this.config.definition },
            totalCount: { text: 'N/A' },
            dataSource: { text: this.config.dataSource }
          }
        ];
        return of({ columns: fallbackColumns, data: fallbackData });
      })
    );
  }

  /**
   * Format date string for display
   */
  private formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString; // Return original if parsing fails
    }
  }
}

